import { Modal } from '@maxtropy/components';
import DMYDateSwitch from '../components/DMYDateSwitch';
import { useState } from 'react';
import { Dayjs } from 'dayjs';
import { getMediumIndicatorStatistics } from '@/api/energyMedium';
import Chart from '../components/Chart';
import styles from './index.module.scss';
import { ModalType, TimeActive } from '../components/DMYDateSwitch/type';
import { useRequest } from 'ahooks';
import { isNil } from 'lodash-es';
import { StatisticsPartition } from '@/api/statistics';
import dayjs from 'dayjs';
import { FormatterItem } from '../components/Chart/chartOptions';

interface FlowDetailProps {
  onCancel?: () => void;
  flowData?: any;
}

const FlowDetail: React.FC<FlowDetailProps> = ({ onCancel, flowData }) => {
  const [rangeDate, setRangeDate] = useState<[Dayjs, Dayjs] | null>(null);
  const [activeKey, setActiveKey] = useState<TimeActive>(TimeActive.THIS_DAY);

  const { data: chartData, loading } = useRequest(
    async () => {
      if (!rangeDate || isNil(flowData?.nodeIdType) || isNil(flowData?.topologyNodeId)) return null;
      // 流量图表数据
      return getMediumIndicatorStatistics({
        from: rangeDate[0].valueOf(),
        to: rangeDate[1].endOf('day').valueOf(),
        indicatorId: 103,
        processEntryOrExitId: flowData.nodeIdType.split('-')[0],
        processId: flowData.topologyNodeId,
        timeResolution:
          activeKey === TimeActive.THIS_DAY
            ? StatisticsPartition.DAY
            : activeKey === TimeActive.THIS_MONTH
            ? StatisticsPartition.MONTH
            : StatisticsPartition.YEAR,
        type: flowData.nodeIdType.split('-')[1],
      });
    },
    {
      refreshDeps: [rangeDate, flowData, activeKey],
      ready: !!rangeDate && !!flowData,
    }
  );

  return (
    <>
      <Modal
        open={true}
        title={'查看详情'}
        size="big"
        maskClosable={false}
        onCancel={onCancel}
        destroyOnClose
        footer={null}
      >
        <DMYDateSwitch
          onRangeDateChange={setRangeDate}
          onActiveKeyChange={setActiveKey}
          switchOption={ModalType.FlowMeter}
        />

        <div className={styles.chartContainer}>
          <Chart
            loading={loading}
            chartData={chartData}
            option={{
              grid: {
                top: 95,
                bottom: 80,
              },
              color: ['#00ADFF'],
              tooltip: {
                formatter(items: FormatterItem[]) {
                  const { axisValue } = items[0];
                  let str: string;
                  // 根据 activeKey 判断颗粒度
                  switch (activeKey) {
                    case 'DAY':
                      str = dayjs(axisValue, 'x').format('MM-DD');
                      break;
                    case 'MONTH':
                      str = dayjs(axisValue, 'x').format('YYYY-MM');
                      break;
                    case 'YEAR':
                    default:
                      str = dayjs(axisValue, 'x').format('YYYY');
                  }
                  // 单位
                  const unit = 'm³';
                  items.forEach(item => {
                    const { seriesName, data, marker } = item;
                    const value = !isNil(data[1]) ? `${data[1].toFixed(2)}${unit}` : '--';
                    str += `<br> ${marker}${seriesName}: ${value}`;
                  });
                  return str;
                },
              },
              xAxis: [
                {
                  axisTick: {
                    show: false,
                  },
                  splitLine: {
                    show: false,
                  },
                  type: 'time',
                  minInterval: activeKey === 'DAY' ? 86400000 : activeKey === 'MONTH' ? 28 * 86400000 : 365 * 86400000,
                  axisLabel: {
                    formatter: (value: number) => {
                      if (activeKey === 'DAY') {
                        return dayjs(value).format('YYYY-MM-DD');
                      }
                      if (activeKey === 'MONTH') {
                        return dayjs(value).format('YYYY-MM');
                      }
                      return dayjs(value).format('YYYY');
                    },
                  },
                },
              ],
              yAxis: {
                type: 'value',
                name: 'm³',
                nameTextStyle: {
                  align: 'right',
                },
                nameGap: 30,
              },
              legend: {
                show: true,
                top: 10,
                right: 10,
                data: [
                  {
                    name: '用水量',
                    icon: 'rect',
                  },
                ],
              },
              series: [
                {
                  type: 'bar',
                  color: ['#00ADFF'],
                  unit: 'm³',
                  timeResolution:
                    activeKey === TimeActive.THIS_DAY ? 'D' : activeKey === TimeActive.THIS_MONTH ? 'M' : 'Y',
                  name: '用水量',
                  data: (chartData?.data ?? [])?.map((item: any) => [item.ts, item.sum]),
                  markLine: {
                    silent: true,
                    symbol: ['none', 'none'],
                    label: {
                      formatter: ({ value }: any) => `平均值: ${value} m³`,
                      position: 'insideEndTop',
                      color: '#FACC58',
                    },
                    lineStyle: {
                      color: '#FACC58',
                      type: 'dashed',
                    },
                    data: [
                      {
                        yAxis:
                          chartData?.data && chartData.data.length
                            ? (
                                chartData.data.reduce((sum: number, item: any) => sum + (item.sum ?? 0), 0) /
                                chartData.data.length
                              ).toFixed(2)
                            : 0,
                      },
                    ],
                  },
                },
              ],
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default FlowDetail;
